/**
 * Backend data for the Design Builder, fetched exactly the way the DVM Layout
 * Builder does it (same SelfServiceDatasetService calls, same entity group and
 * hidden-column config):
 *
 * - datasets → `getEntities(entityGroup)`
 * - columns  → `getEntityAttributes(dataset, entityGroup)`
 *
 * A bound text's value does NOT come from rows: it is registered as a KPI
 * component and read from `/components/compute`, so it agrees with the charts
 * on aggregation, filters and freshness. See `DesignRuntimeService.connectText`.
 *
 * Raw rows (`loadRows` / `rows`) remain for the one widget that needs the rows
 * themselves rather than an aggregate: the zone slider's zone lookup table
 * (one row per coloured band). See `renderers/zone-slider-binding.utils.ts`.
 */

import { Injectable, Signal, computed, inject, signal } from '@angular/core';

import type { BuilderLayoutConfig, LabelValueOption } from '../../shared/self-service-config.types';
type Row = Record<string, unknown>;
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import type { DesignNode, TextDataBinding, TextFormula } from '../design-builder.types';
import { DesignRuntimeService, TEXT_VALUE_AGGREGATION } from '../data/design-runtime.service';
import { DesignDocumentStore } from '../state/design-document.store';
import { flattenNodes } from '../utils/design-node.utils';
import { evaluateFormula, type FormulaResult } from '../utils/design-formula.utils';
import { isInputWidget } from '../utils/design-input-value.utils';
import { STATUS_COUNT_AGGREGATION, statusCountOf } from '../utils/design-status-count.utils';
export { STATUS_COUNT_AGGREGATION, statusCountOf } from '../utils/design-status-count.utils';
import { applyZoneSliderBinding, zoneSliderBindingOf, zoneSliderStaticData } from '../renderers/zone-slider-binding.utils';
import { resolveZoneSliderModel, type ZoneSliderData } from '../../../global-components/zone-slider/zone-slider.utils';

export type LoadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'error'; message: string };

/** The aggregations the backend understands, as the KPI card offers them. */
export const TEXT_AGGREGATIONS: LabelValueOption[] = [
  { label: 'Sum', value: 'SUM' },
  { label: 'Average', value: 'AVG' },
  { label: 'Count', value: 'COUNT' },
  { label: 'Distinct count', value: 'COUNT_DISTINCT' },
  { label: 'Minimum', value: 'MIN' },
  { label: 'Maximum', value: 'MAX' },
  { label: 'First value', value: 'FIRST' },
  { label: 'Last value', value: 'LAST' },
  // Whole days since the column's earliest date, e.g. the oldest pending approval.
  { label: 'Oldest age (days)', value: 'OLDEST_AGE_DAYS' },
  // Words / sentences (comments, justifications) shown as they are - see TEXT_VALUE_AGGREGATION.
  { label: 'Text - show the value as is', value: TEXT_VALUE_AGGREGATION },
];


@Injectable()
export class DesignDataService {
  private readonly datasetService = inject(SelfServiceDatasetService);
  /** Canvas filters (dropdowns / cross-filter); absent outside the builder. */
  private readonly runtime = inject(DesignRuntimeService, { optional: true });

  private readonly layout = signal<BuilderLayoutConfig | null>(null);
  private readonly datasetsState = signal<LoadState<string[]>>({ status: 'idle' });
  private readonly columnsState = signal<Record<string, LoadState<string[]>>>({});
  private readonly rowsState = signal<Record<string, LoadState<Row[]>>>({});

  readonly datasets: Signal<LoadState<string[]>> = this.datasetsState.asReadonly();

  readonly aggregations = signal<LabelValueOption[]>(TEXT_AGGREGATIONS).asReadonly();
  /** A text's Summary choices: the aggregations plus "Count per status" (texts only). */
  readonly textAggregations = signal<LabelValueOption[]>([
    ...TEXT_AGGREGATIONS,
    { label: 'Count per status (STATUS_COUNTS)', value: STATUS_COUNT_AGGREGATION },
  ]).asReadonly();

  /** The builder config supplies the entity group, hidden columns and compute URL. */
  setLayout(layout: BuilderLayoutConfig | null): void {
    this.layout.set(layout);
  }

  // ── Datasets & columns ─────────────────────────────────────────────────────

  loadDatasets(force = false): void {
    const current = this.datasetsState().status;
    if (!force && (current === 'loading' || current === 'ready')) return;
    this.datasetsState.set({ status: 'loading' });
    this.datasetService.getEntities(this.entityGroup()).subscribe({
      next: (names) => this.datasetsState.set({ status: 'ready', value: [...names].sort((a, b) => a.localeCompare(b)) }),
      error: (error: unknown) => this.datasetsState.set({ status: 'error', message: httpMessage(error, 'Could not load datasets.') }),
    });
  }

  columns(dataset: string): LoadState<string[]> {
    return this.columnsState()[dataset] ?? { status: 'idle' };
  }

  loadColumns(dataset: string): void {
    if (!dataset) return;
    const current = this.columns(dataset).status;
    if (current === 'loading' || current === 'ready') return;
    this.patchColumns(dataset, { status: 'loading' });
    this.datasetService.getEntityAttributes(dataset, this.entityGroup()).subscribe({
      next: (names) => {
        this.patchColumns(dataset, { status: 'ready', value: this.visibleColumns(names) });
        const rows = this.rows(dataset);
        if (rows.status === 'ready') this.addSystemColumns(dataset, rows.value);
      },
      error: (error: unknown) => this.patchColumns(dataset, { status: 'error', message: httpMessage(error, 'Could not load columns.') }),
    });
  }

  // ── Rows (zone slider) ─────────────────────────────────────────────────────

  rows(dataset: string): LoadState<Row[]> {
    return this.rowsState()[dataset] ?? { status: 'idle' };
  }

  /** Same `/{entity}/dynamic_filter` call the Layout Builder makes, once per dataset. */
  loadRows(dataset: string): void {
    if (!dataset) return;
    const current = this.rows(dataset).status;
    if (current === 'loading' || current === 'ready') return;
    this.patchRows(dataset, { status: 'loading' });
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        this.patchRows(dataset, { status: 'ready', value: list });
        this.addSystemColumns(dataset, list);
      },
      error: (error: unknown) => this.patchRows(dataset, { status: 'error', message: httpMessage(error, 'Could not load data.') }),
    });
  }

  // ── Bound values ───────────────────────────────────────────────────────────

  /**
   * A bound text's value, from `/components/compute`.
   *
   * Until the text is connected it has no componentId and no value: the old
   * path pulled the whole table over `dynamic_filter` and aggregated it in the
   * browser, which disagreed with the charts on aggregation, on filter
   * semantics and on freshness. Connecting registers it as a KPI component.
   */
  value(binding: TextDataBinding, _nodeId = ''): LoadState<number | string | null> {
    if (!binding.dataset || !binding.field) return { status: 'idle' };
    if (!binding.componentId) return { status: 'idle' };
    const state = this.runtime?.textValues()[binding.componentId] ?? { status: 'idle' };
    // Count per status: the answer holds every status, so changing the Status
    // shows its count at once, without asking the API again.
    if (binding.aggregation === STATUS_COUNT_AGGREGATION && state.status === 'ready' && state.counts) {
      return { status: 'ready', value: statusCountOf(state.counts, binding.statusValue) };
    }
    return state;
  }

  /** First paint after a reload: a connected text has no value yet, so fetch it. */
  ensureValue(binding: TextDataBinding, title = ''): void {
    if (!binding.componentId || !binding.dataset || !binding.field) return;
    if (this.runtime?.textValues()[binding.componentId]) return;
    this.runtime?.connectText(binding.componentId, binding, title);
  }

  // ── Text formulas ──────────────────────────────────────────────────────────

  private readonly doc = inject(DesignDocumentStore, { optional: true });
  /** Every layer on every page — a formula may reference any of them by name. */
  private readonly allNodes = computed(() =>
    flattenNodes(this.doc?.document().pages.flatMap((page) => page.nodes) ?? []));

  /**
   * A text formula worked out in the browser from typed inputs, bound values
   * and other texts. Re-runs whenever any of those change (it reads signals).
   */
  formula(formula: Pick<TextFormula, 'expression'>, nodeId = ''): FormulaResult {
    return evaluateFormula(formula.expression, {
      nodes: this.allNodes(),
      readBound: (binding) => {
        const state = this.value(binding);
        return state.status === 'ready' ? state.value : null;
      },
      readInput: (node) => this.inputValue(node),
    }, new Set(nodeId ? [nodeId] : []));
  }

  /** The statuses a "Count per status" text's last answer had (to pick from). */
  statusChoices(binding: TextDataBinding | null | undefined): string[] {
    if (!binding?.componentId) return [];
    const state = this.runtime?.textValues()[binding.componentId];
    return state?.status === 'ready' && state.counts ? Object.keys(state.counts) : [];
  }

  /** Layers a formula can use, by name: inputs and texts. */
  formulaSources(excludeId = ''): DesignNode[] {
    return this.allNodes().filter((node) => node.id !== excludeId && (node.type === 'TEXT' || isInputWidget(node)));
  }

  /** What an input widget holds: typed this session, else its built / default value. */
  private inputValue(node: DesignNode): number | string | null {
    const typed = this.runtime?.inputValues()[node.id];
    if (typed !== undefined) return typed;
    // "Fill from data": the column's value for the current filters.
    const fromData = this.runtime?.inputDataValues()[node.id];
    if (fromData?.status === 'ready' && fromData.value !== null) return fromData.value;
    const tile = this.runtime?.tiles().find((item) => item.id === node.id);
    const built = tile?.inputControl?.value;
    if (built !== undefined && built !== null && !Array.isArray(built)) return built as number | string;
    const widget = node.widget;
    if (widget?.layoutType === 'ZONE_SLIDER') return this.zoneSliderValue(node);
    if (widget?.layoutType === 'SLIDER') {
      // Same nesting `app-mat-slider` reads: `data.data.data` (tile shape) or `data.data`.
      const outer = widget.data?.['data'] as Record<string, unknown> | undefined;
      const config = (outer?.['data'] ?? outer) as Record<string, unknown> | undefined;
      const end = config?.['rangeEnd'];
      if (typeof end === 'number' || typeof end === 'string') return end;
    }
    const initial = widget?.data?.['value'];
    return typeof initial === 'number' || typeof initial === 'string' ? initial : null;
  }

  /**
   * What a zone slider shows before anyone drags it — built exactly like its
   * renderer does (settings, bound value / range from compute, clamped to range).
   */
  private zoneSliderValue(node: DesignNode): number | null {
    const widget = node.widget!;
    const compute = this.runtime?.zoneValues()[node.id];
    const data = applyZoneSliderBinding(
      zoneSliderStaticData(widget),
      zoneSliderBindingOf(widget.binding),
      (dataset) => {
        const state = this.rows(dataset);
        return state.status === 'ready' ? state.value : null;
      },
      compute?.status === 'ready' ? compute.values : null,
    );
    const value = resolveZoneSliderModel(data as ZoneSliderData).value;
    return Number.isFinite(value) ? value : null;
  }

  /** Re-compute every connected text and re-fetch rows (the panel's refresh button). */
  refreshValues(): void {
    this.runtime?.refreshTexts();
    this.runtime?.refreshZoneSliders();
    this.rowsState.set({});
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  private entityGroup(): string {
    return this.layout()?.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
  }

  private visibleColumns(names: string[]): string[] {
    const hidden = new Set((this.layout()?.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((name) => name.toLowerCase()));
    return [...new Set(names)].filter((name) => !hidden.has(name.toLowerCase()));
  }

  /** System attributes (created_by, updated_by …) are on the rows but not in
   *  `getEntityAttributes`; once rows are in, list them as columns too. */
  private addSystemColumns(dataset: string, rows: Row[]): void {
    const state = this.columns(dataset);
    if (state.status !== 'ready' || !rows.length) return;
    const names = this.visibleColumns([...state.value, ...rows.slice(0, 50).flatMap((row) => Object.keys(row ?? {}))]);
    const known = new Set<string>();
    const merged = names.filter((name) => {
      const lower = name.toLowerCase();
      if (known.has(lower)) return false;
      known.add(lower);
      return true;
    });
    if (merged.length > state.value.length) this.patchColumns(dataset, { status: 'ready', value: merged });
  }

  private patchColumns(dataset: string, state: LoadState<string[]>): void {
    this.columnsState.update((all) => ({ ...all, [dataset]: state }));
  }

  private patchRows(dataset: string, state: LoadState<Row[]>): void {
    this.rowsState.update((all) => ({ ...all, [dataset]: state }));
  }
}

/** The number-format part of a bound text or a formula. */
export type NumberFormat = Pick<TextDataBinding, 'scale' | 'decimals' | 'prefix' | 'suffix'>;

/** The displayed string for a bound value, honouring scale / decimals / affixes. */
export function formatBoundValue(binding: NumberFormat, value: number | string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return `${binding.prefix ?? ''}${value}${binding.suffix ?? ''}`;

  const scale = binding.scale ?? 'auto';
  const divisor = scaleDivisor(scale, numeric);
  const unit = divisor === 1e9 ? 'B' : divisor === 1e6 ? 'M' : divisor === 1e3 ? 'K' : '';
  const decimals = binding.decimals ?? (unit ? 1 : 0);
  const body = (numeric / divisor).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${binding.prefix ?? ''}${body}${unit}${binding.suffix ?? ''}`;
}

/**
 * The initials of a name, for an avatar: "John Doe" → "JD", an email's name
 * part "talari.vs@…" → "TV", one word "Navya" → "NA". '' when there is none.
 */
export function initialsOf(text: string): string {
  const raw = String(text ?? '').trim();
  const name = raw.includes('@') ? raw.slice(0, raw.indexOf('@')) : raw;
  const words = name.split(/[\s._\-,]+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (!words.length) return '';
  const first = (word: string) => [...word].find((char) => /[\p{L}\p{N}]/u.test(char)) ?? '';
  const letters = words.length > 1 ? first(words[0]) + first(words[words.length - 1]) : [...words[0]].filter((char) => /[\p{L}\p{N}]/u.test(char)).slice(0, 2).join('');
  return letters.toUpperCase();
}

/** `{{value}}` in the text is replaced; a text without the token shows the value alone. */
export function renderBoundText(template: string, formatted: string): string {
  return template.includes('{{value}}') ? template.split('{{value}}').join(formatted) : formatted;
}

function scaleDivisor(scale: string, value: number): number {
  switch (scale) {
    case 'K': return 1e3;
    case 'M': return 1e6;
    case 'B': return 1e9;
    case 'none': return 1;
    default: {
      const magnitude = Math.abs(value);
      return magnitude >= 1e9 ? 1e9 : magnitude >= 1e6 ? 1e6 : magnitude >= 1e3 ? 1e3 : 1;
    }
  }
}

function httpMessage(error: unknown, fallback: string): string {
  const status = (error as { status?: number })?.status;
  const detail = (error as { error?: { message?: string } })?.error?.message;
  if (detail) return `${fallback} ${detail}`;
  return status ? `${fallback} (error ${status})` : fallback;
}
